import { Checkbox } from '@teamsnap/snap-ui';
import { isEditableInstallment } from 'components/common/balance-adjustment';
import { Adjustment } from 'components/order-details/adjust-order-drawer';
import { DateFormat, DateService, getFormattedAmount } from 'frontend-toolkit';
import { PendingPayment, PreviewLineItems } from 'hooks/postRegEditDrawer';
import * as React from 'react';
import { Installment } from '../../types';

const getAcceptText = (
  previewLineItems: PreviewLineItems,
  pendingPayment: PendingPayment,
  balanceChange: number,
  nextInstallment?: Partial<Installment>,
  adjustment?: Adjustment
) => {
  if (!pendingPayment) {
    return '';
  }
  const { newBalance, currentBalance, lineItemTotal } = previewLineItems || {};
  const { savedPaymentMethod } = pendingPayment;
  const hasSavedPaymentMethod = !!savedPaymentMethod;
  let direction = balanceChange > 0 ? 'increase' : 'decrease';
  switch (adjustment?.type) {
    case 'add':
      direction = 'increase';
      break;
    case 'reduce':
      direction = 'decrease';
      break;
    case 'payment_schedule':
      return 'I have obtained consent from the registrant to revise the payment schedule. An email will be sent to the registrant with a summary of the updates.';
  }
  const balanceChangeAmount =
    (lineItemTotal || 0) < 0
      ? Math.abs(currentBalance)
      : Math.abs(newBalance - currentBalance);

  if (newBalance < 0 && !adjustment) {
    return `I have obtained consent from the registrant to update the order and decrease by ${getFormattedAmount(
      balanceChangeAmount / 100
    )}`;
  }

  const paymentMethodString =
    hasSavedPaymentMethod && nextInstallment?.scheduledDate
      ? `The saved payment method will be charged ${getFormattedAmount(
          nextInstallment.amount / 100
        )} on ${DateService.format(
          DateService.adjustForTimezone(
            new Date(nextInstallment?.scheduledDate)
          ),
          DateFormat.MM_DD_YYYY
        )}.`
      : '';
  const emailString =
    'An email will be sent to the registrant with a summary of the updates.';
  return `I have obtained consent from the registrant to update the order and ${direction} by ${getFormattedAmount(
    balanceChangeAmount / 100
  )}. ${paymentMethodString} ${emailString}`;
};

const getNextInstallment = (pendingPayment: PendingPayment) => {
  const scheduledInstallments =
    pendingPayment?.installments?.filter(isEditableInstallment) || [];
  return scheduledInstallments?.length > 0
    ? scheduledInstallments[0]
    : undefined;
};

interface Props {
  previewLineItems: PreviewLineItems;
  pendingPayment: PendingPayment;
  accepted: boolean;
  setAccepted: (ac: boolean) => void;
  adjustment?: Adjustment;
}

export const ConsentCheckbox = ({
  previewLineItems,
  pendingPayment,
  setAccepted,
  accepted,
  adjustment,
}: Props) => {
  const balanceChange =
    previewLineItems?.newBalance - previewLineItems?.currentBalance || 0;
  const nextInstallment = getNextInstallment(pendingPayment);
  const acceptText = getAcceptText(
    previewLineItems,
    pendingPayment,
    balanceChange,
    nextInstallment,
    adjustment
  );
  if (adjustment?.type !== 'payment_schedule' && balanceChange === 0) {
    return null;
  }
  return (
    <Checkbox
      name="accepted"
      checked={accepted}
      onChange={(e) => {
        setAccepted(e.target.checked);
      }}
      value="consent-for-payment-updates"
      data-testid="ConsentCheckbox--accept"
    >
      <span>{acceptText}</span>
    </Checkbox>
  );
};
